import { FoldHeader } from '@components/FoldHeader/FoldHeader';
import { ApiError } from '@lib/network/api';
import { compilationMedia, type CompilationInfo } from '@lib/network/rendering';
import { Skeleton } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import css from './MediaPane.module.scss';
import { usePostForm } from './PostFormStore';

/** The file. Mounted only while on screen: `preload="auto"` pulls it as soon as it is. */
const MediaPlayer = ({
  render,
  onFirstPlay,
  onRetry,
}: {
  render: CompilationInfo;
  onFirstPlay: () => void;
  /** Mount a fresh player, with a fresh URL: the failure may have been the token's. */
  onRetry: () => void;
}) => {
  // Held for as long as this render is on the page (the pane keys the player by render id): see `compilationMedia`.
  const [media] = useState(() => {
    const media = compilationMedia(render.id, userStore.token);
    if (!media) throw new Error('compose page without a session');
    return media;
  });
  const [failed, setFailed] = useState(false);
  return (
    <div className={css.player}>
      <video
        className={css.video}
        style={
          render.width && render.height
            ? { aspectRatio: `${render.width} / ${render.height}` }
            : undefined
        }
        src={media.video}
        poster={media.poster}
        controls
        playsInline
        preload="auto"
        aria-label={`Media preview: ${render.title ?? 'untitled render'}`}
        onPlay={onFirstPlay}
        onError={(e) => {
          console.error('Failed to load the render media', e.currentTarget.error);
          setFailed(true);
        }}
      />
      {failed && (
        <div className={css.playerError}>
          <span>Couldn’t load the media.</span>
          <ActionButton variant="secondary" onClick={onRetry}>
            Retry
          </ActionButton>
        </div>
      )}
    </div>
  );
};

/**
 * The render the post is about: its title and size, and the media itself.
 * Beside the form it is always on screen; above it (`stacked`) the header
 * folds it and it starts folded, so a narrow screen loads nothing until
 * asked. `null` for `stacked` is "not measured yet", which shows nothing
 * either: the columns form for a frame would start the download.
 */
export const MediaPane = observer(function MediaPane({
  stacked,
  portrait,
}: {
  stacked: boolean | null;
  /** Taller than wide: takes less column width and is bounded by height instead. */
  portrait: boolean;
}) {
  const form = usePostForm();
  const { render, renderError } = form;
  const [collapsed, setCollapsed] = useState(true);
  // Bumped by the player's Retry: a new key mounts a new player.
  const [attempt, setAttempt] = useState(0);
  const played = useRef(false);
  // Once the columns layout has shown the media, it stays shown: a frame
  // narrowing under the threshold (a tablet turning) folds the card open
  // rather than tearing down what was playing.
  useEffect(() => {
    if (stacked === false) setCollapsed(false);
  }, [stacked]);
  const playable = render !== null && !render.failed;
  const showVideo = playable && (stacked === false || (stacked === true && !collapsed));

  const onFirstPlay = () => {
    if (played.current) return;
    played.current = true;
    trackingStore.scheduleMediaPreviewed({
      page: form.mode.kind,
      layout: stacked ? 'stacked' : 'columns',
    });
  };

  let info: ReactNode;
  if (form.postWithoutRender) {
    info = (
      <>
        <span className={css.title}>No media</span>
        <span className={css.meta}>This post has no media attached.</span>
      </>
    );
  } else if (renderError) {
    // 404: gone or never existed. 403: exists, owned by someone else. Anything else: try again.
    const status = renderError instanceof ApiError ? renderError.status : null;
    info = (
      <>
        <span className={css.title}>
          {status === 404
            ? 'Render not found'
            : status === 403
              ? 'This render belongs to another account'
              : 'Could not read the render'}
        </span>
        <span className={css.meta}>
          {status === 404
            ? 'It may have been deleted.'
            : status === 403
              ? 'Only its owner can schedule it.'
              : 'Try again later.'}
        </span>
        {status !== 404 && status !== 403 && (
          <ActionButton
            variant="secondary"
            className={css.retry}
            onClick={() => form.reloadRender()}
          >
            Retry
          </ActionButton>
        )}
      </>
    );
  } else if (!render) {
    info = (
      <>
        <Skeleton variant="text" width={220} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
        <Skeleton variant="text" width={120} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
      </>
    );
  } else {
    info = (
      <>
        <span className={css.title}>{render.title ?? 'Untitled render'}</span>
        <span className={css.meta}>
          {render.failed
            ? 'This render failed and cannot be published.'
            : render.width && render.height
              ? `Media · ${render.width} × ${render.height}`
              : 'Media'}
        </span>
      </>
    );
  }

  return (
    <div
      className={classNames(css.pane, stacked !== false && css.stacked, portrait && css.portrait)}
    >
      <FoldHeader
        foldable={stacked === true && playable}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        className={css.header}
      >
        <div className={css.info}>{info}</div>
      </FoldHeader>
      {showVideo && render && (
        <MediaPlayer
          key={`${render.id}:${attempt}`}
          render={render}
          onFirstPlay={onFirstPlay}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      )}
    </div>
  );
});
